import React, { useEffect, useState } from "react";
import { useParams, useLocation, useNavigate } from "react-router-dom";
import {
    GetEducationSectionsApi,
    GetUserProgressApi,
} from "../../../api/request/knowledgeHub.js";

const EducationMaterialDetail = () => {
    const { materialId } = useParams();
    const navigate = useNavigate();
    const location = useLocation();
    const [sections, setSections] = useState([]);
    const [progress, setProgress] = useState(null);
    const [isLoading, setIsLoading] = useState(true);

    const material = location.state?.material || {};
    const materialTitle = material.title || "Education Material";
    const materialDescription =
        material.description ||
        "Free online course covering the essentials of trading the financial market.";

    useEffect(() => {
        const fetchData = async () => {
            if (!materialId) return;
            try {
                setIsLoading(true);
                const [sectionsRes, progressRes] = await Promise.all([
                    GetEducationSectionsApi(materialId),
                    GetUserProgressApi(materialId),
                ]);

                const sectionsData = sectionsRes?.data?.data || [];
                const progressData = progressRes?.data?.data || null;

                const progressMap = {};
                if (progressData?.sectionWiseCompletedUnits) {
                    progressData.sectionWiseCompletedUnits.forEach((item) => {
                        progressMap[item.sectionId] = {
                            completedUnits: item.completedUnits,
                            totalUnits: item.totalUnits ?? 0,
                        };
                    });
                }

                const mergedSections = sectionsData
                    .sort((a, b) => a.displayOrder - b.displayOrder)
                    .map((section, index) => {
                        const sectionProgress = progressMap[section.id];
                        const completedUnits = sectionProgress?.completedUnits ?? 0;
                        const totalUnits =
                            sectionProgress?.totalUnits || section.units || 0;
                        const isInProgress = completedUnits > 0 && completedUnits < totalUnits;
                        const isCompleted = totalUnits > 0 && completedUnits === totalUnits;

                        return {
                            ...section,
                            sectionNumber: index + 1,
                            completedUnits,
                            totalUnits,
                            isInProgress,
                            isCompleted,
                        };
                    });

                setSections(mergedSections);
                setProgress(progressData);
            } catch (error) {
                console.error("Failed to fetch education data:", error);
                setSections([]);
            } finally {
                setIsLoading(false);
            }
        };

        fetchData();
    }, [materialId]);

    const completedSections = progress?.completedSections ?? 0;
    const totalSections = progress?.totalSections ?? sections.length;

    if (isLoading) {
        return (
            <div className="animate-pulse space-y-6">
                <div className="h-8 bg-gray-200 rounded w-3/4"></div>
                <div className="h-4 bg-gray-200 rounded w-1/2"></div>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 mt-8">
                    {[1, 2, 3].map((i) => (
                        <div
                            key={i}
                            className="h-40 bg-gray-200 rounded-xl"
                        ></div>
                    ))}
                </div>
            </div>
        );
    }

    return (
        <div className="space-y-6">
            {/* Header */}
            <div className="flex gap-3">
                <div
                    className="w-2 h-10 rounded shrink-0"
                    style={{
                        backgroundColor: "var(--theme, #6366f1)",
                    }}
                />
                <div>
                    <h1 className="text-2xl font-bold text-[#2F2B3D]">
                        {materialTitle}
                    </h1>
                    <p className="text-[#7A7A7A] mt-1">{materialDescription}</p>
                </div>
            </div>

            {/* Progress Section */}
            <div className="flex flex-wrap justify-between items-center gap-4 border-b border-gray-200 pb-4">
                <div>
                    <p className="font-bold text-[#2F2B3D]">Your progress</p>
                    <p className="text-sm text-[#7A7A7A] mt-0.5">
                        {completedSections} of {totalSections} finished
                    </p>
                </div>
            </div>

            {/* Section Cards Grid */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
                {sections.map((section) => (
                    <div
                        key={section.id}
                        className={`rounded-xl p-5 transition-colors ${
                            section.isCompleted
                                ? "bg-green-600"
                                : section.isInProgress
                                    ? "bg-amber-50 border-2 border-amber-300"
                                    : "bg-gray-50 border border-gray-200"
                        }`}
                    >
                        <p
                            className={`text-xs font-bold uppercase tracking-wide mb-2 ${
                                section.isCompleted ? "text-white" : "text-[#2F2B3D]"
                            }`}
                        >
                            Section {section.sectionNumber}
                        </p>
                        <h3
                            className={`text-base font-bold mb-3 ${
                                section.isCompleted ? "text-white" : "text-[#2F2B3D]"
                            }`}
                        >
                            {section.title}
                        </h3>
                        <p
                            className={`text-sm mb-4 ${
                                section.isCompleted ? "text-white" : "text-[#7A7A7A]"
                            }`}
                        >
                            {section.isCompleted
                                ? `Finished all ${section.totalUnits} units`
                                : section.isInProgress
                                    ? `${section.completedUnits} / ${section.totalUnits} units done`
                                    : `${section.totalUnits} units`}
                        </p>
                        <button
                            type="button"
                            onClick={() => {
                                navigate("/education/section/" + section.id, {
                                    state: {
                                        section: {
                                            ...section,
                                            materialId,
                                        },
                                        material: material,
                                    },
                                });
                            }}
                            className={`w-full py-2.5 px-4 rounded-full font-medium text-sm transition-colors flex items-center justify-center gap-2 ${
                                section.isCompleted
                                    ? "bg-white text-green-600 hover:bg-green-50"
                                    : section.isInProgress
                                        ? "bg-amber-400 hover:bg-amber-500 text-[#2F2B3D]"
                                        : "bg-blue-50 hover:bg-blue-100 text-blue-600"
                            }`}
                        >
                            {section.isCompleted ? (
                                <>
                                    Start again
                                    <svg
                                        xmlns="http://www.w3.org/2000/svg"
                                        viewBox="0 0 24 24"
                                        fill="currentColor"
                                        className="w-4 h-4"
                                    >
                                        <path
                                            fillRule="evenodd"
                                            d="M4.755 10.059a7.5 7.5 0 0 1 12.548-3.364l1.903 1.903h-3.183a.75.75 0 1 0 0 1.5h4.992a.75.75 0 0 0 .75-.75V4.356a.75.75 0 0 0-1.5 0v3.18l-1.9-1.9A9 9 0 0 0 3.306 9.67a.75.75 0 0 0 1.45.388Zm15.408 3.352a.75.75 0 0 0-.919.53 7.5 7.5 0 0 1-12.548 3.364l-1.902-1.903h3.183a.75.75 0 0 0 0-1.5H2.984a.75.75 0 0 0-.75.75v4.992a.75.75 0 0 0 1.5 0v-3.18l1.9 1.9a9 9 0 0 0 15.059-4.035.75.75 0 0 0-.53-.918Z"
                                            clipRule="evenodd"
                                        />
                                    </svg>
                                </>
                            ) : section.isInProgress ? (
                                "Continue →"
                            ) : (
                                "Start →"
                            )}
                        </button>
                    </div>
                ))}
            </div>

            {sections.length === 0 && (
                <p className="text-center text-[#7A7A7A] py-12">
                    No sections available.
                </p>
            )}
        </div>
    );
};

export default EducationMaterialDetail;
